import { useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { Map, MapPin } from "lucide-react";
import { LGoButton, LGoFormField } from "@/ui/branded";
import { FormSection } from "@/ui/patterns";
import { blankToNull } from "@/domain/validation";
import type { BusinessProfileInput } from "@/domain/business/schema";
import { LocationPicker } from "./LocationPicker";

const isCoordinate = (value: number | null): value is number => blankToNull(value) !== null;

interface LocationSectionProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export function LocationSection({ isOpen, onOpenChange }: LocationSectionProps) {
  const { watch, setValue } = useFormContext<BusinessProfileInput>();
  const lat = watch("locationLatitude");
  const lng = watch("locationLongitude");
  const pinnedLocation = useMemo(
    () => (isCoordinate(lat) && isCoordinate(lng) ? { lat, lng } : undefined),
    [lat, lng],
  );

  return (
    <>
      <FormSection
        title={
          <span className="inline-flex items-center gap-2">
            <Map className="h-4 w-4 text-muted-foreground" /> Poloha na mapě
          </span>
        }
        columns={2}
      >
        <LGoFormField
          name="locationLatitude"
          label="Zeměpisná šířka"
          required
          type="number"
          placeholder="např. 50.0755"
        />
        <LGoFormField
          name="locationLongitude"
          label="Zeměpisná délka"
          required
          type="number"
          placeholder="např. 14.4378"
        />
        <div className="md:col-span-2">
          <LGoButton
            type="button"
            variant="secondary"
            onClick={() => onOpenChange(true)}
            iconLeft={<MapPin />}
          >
            Vybrat na mapě
          </LGoButton>
        </div>
      </FormSection>

      <LocationPicker
        open={isOpen}
        onOpenChange={onOpenChange}
        initialLocation={pinnedLocation}
        onLocationSelect={(loc) => {
          setValue("locationLatitude", loc.lat, { shouldDirty: true, shouldValidate: true });
          setValue("locationLongitude", loc.lng, { shouldDirty: true, shouldValidate: true });
        }}
      />
    </>
  );
}
